import { useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useMemo, type CSSProperties } from "react";
import { navTabsListStyles, navTabsScrollableListStyles } from "./navTabsLayout";

function mobileNavTabsListBase(listOverrides?: CSSProperties) {
  const {
    // Callers often pass desktop equal-column templates; never let those crush mobile stacking.
    gridTemplateColumns: _ignoredGridTemplateColumns,
    display: _ignoredDisplay,
    ...safeOverrides
  } = listOverrides ?? {};

  return {
    ...safeOverrides,
    display: "grid" as const,
    gridTemplateColumns: "1fr",
    width: (safeOverrides.width ?? "100%") as CSSProperties["width"],
    maxWidth: (safeOverrides.maxWidth ?? "100%") as CSSProperties["maxWidth"],
    minWidth: (safeOverrides.minWidth ?? 0) as CSSProperties["minWidth"],
    alignItems: (safeOverrides.alignItems ?? "stretch") as CSSProperties["alignItems"],
    boxShadow: "none" as const,
    gap: safeOverrides.gap ?? 0,
  };
}

type UseNavTabsStackLayoutOptions = {
  /**
   * When true, keep equal-width columns on mobile instead of stacking or dropdown.
   * Use only for short tab sets that fit a narrow viewport (e.g. Discover Explore — 3 tabs).
   */
  equalColumnsOnMobile?: boolean;
};

/**
 * Tab nav policy (AppShell pages with sidebar):
 * - Dropdown (`isCompactTabNav`): viewport < sm, OR (< lg AND tabCount >= 5), OR (< xl AND tabCount >= 7)
 * - Stacked grid: viewport < sm AND equalColumnsOnMobile is false AND page still renders inline Tabs
 * - Full-width equal row: inline Tabs with tabCount >= 5 on wider viewports (labels wrap)
 * - Equal grid: few tabs on wide viewports
 *
 * `isMobile` is kept as an alias for `isCompactTabNav` (use MobileNavTabsSelect when true).
 */
export function useNavTabsStackLayout(
  columnCount: number,
  listOverrides?: CSSProperties,
  options?: UseNavTabsStackLayoutOptions,
) {
  const theme = useMantineTheme();
  const equalColumnsOnMobile = options?.equalColumnsOnMobile === true;

  const belowSm = useMediaQuery(
    `(max-width: calc(${theme.breakpoints.sm} - 0.0625em))`,
    false,
    { getInitialValueInEffect: true },
  );
  const belowLg = useMediaQuery(
    `(max-width: calc(${theme.breakpoints.lg} - 0.0625em))`,
    false,
    { getInitialValueInEffect: true },
  );
  const belowXl = useMediaQuery(
    `(max-width: calc(${theme.breakpoints.xl} - 0.0625em))`,
    false,
    { getInitialValueInEffect: true },
  );

  const isStackedMobile = belowSm === true && !equalColumnsOnMobile;
  const isCompactTabNav =
    isStackedMobile ||
    (belowLg === true && columnCount >= 5) ||
    (belowXl === true && columnCount >= 7);

  /** @deprecated alias — use isCompactTabNav; true when pages should render MobileNavTabsSelect */
  const isMobile = isCompactTabNav;

  const useScrollableInlineTabs =
    !isCompactTabNav && !isStackedMobile && columnCount >= 5;

  const tabsListStyles = useMemo(() => {
    if (isStackedMobile) {
      return { list: mobileNavTabsListBase(listOverrides) };
    }
    if (belowSm && equalColumnsOnMobile) {
      return navTabsListStyles(columnCount, {
        ...listOverrides,
        width: listOverrides?.width ?? "100%",
        maxWidth: listOverrides?.maxWidth ?? "100%",
        gridTemplateColumns:
          listOverrides?.gridTemplateColumns ??
          `repeat(${Math.max(columnCount, 1)}, minmax(0, 1fr))`,
        alignItems: listOverrides?.alignItems ?? "stretch",
      });
    }
    if (useScrollableInlineTabs) {
      return navTabsScrollableListStyles({
        ...listOverrides,
        gridTemplateColumns:
          listOverrides?.gridTemplateColumns ??
          `repeat(${Math.max(columnCount, 1)}, minmax(0, 1fr))`,
      });
    }
    return navTabsListStyles(columnCount, listOverrides);
  }, [
    isStackedMobile,
    belowSm,
    equalColumnsOnMobile,
    useScrollableInlineTabs,
    columnCount,
    listOverrides,
  ]);

  const allowTabLabelWrap =
    isStackedMobile || (belowSm && equalColumnsOnMobile) || useScrollableInlineTabs;

  const tabsRootStyles = useMemo(
    () => ({
      tab: {
        whiteSpace: (allowTabLabelWrap ? "normal" : "nowrap") as "normal" | "nowrap",
        overflow: "visible" as const,
        ...(allowTabLabelWrap ? { textAlign: "center" as const } : {}),
      },
      tabLabel: allowTabLabelWrap
        ? {
            overflow: "visible" as const,
            textOverflow: "clip" as const,
            whiteSpace: "normal" as const,
            textAlign: "center" as const,
          }
        : undefined,
    }),
    [allowTabLabelWrap],
  );

  return {
    isMobile,
    isCompactTabNav,
    isStackedMobile,
    belowSm,
    useScrollableInlineTabs,
    tabsListStyles,
    tabsRootStyles,
  };
}
